import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Bookmark, SubscriptionEnum, User } from "../lib/types";
import { ReloadIcon } from "@radix-ui/react-icons";
import { useCallback, useState } from "react";
import { useEffect } from "react";
import { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import { DataTable } from "./ui/data-table";
import { getUserBookmarks } from "../lib/api";
import { ScrollArea } from "./ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { getSubscriptionDisplayName } from "../lib/utils";

export const BookmarkTableColumns: ColumnDef<Bookmark>[] = [
    {
        accessorKey: "bookmark_id",
        header: "bookmark_id",
    },
    {
        accessorKey: "answer_id",
        header: "answer_id",
    },
    {
        accessorKey: "created_at",
        header: "created_at",
        cell: ({row}) => {
            return <span>{new Date(row.original.created_at + 'Z' ).toLocaleString(navigator.language)}</span>
          }
    },
]

export const bookmarkTableFilterColumns: ColumnFiltersState = [
    {
      id: "answer_id",
      value: "",
    },
  ];

export default function Bookmarks({ selectedUser, sessionType, setSessionType }: { selectedUser: User | null, sessionType: SubscriptionEnum, setSessionType: (sessionType: SubscriptionEnum) => void }) {
    const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
    const [userId, setUserId] = useState<number>();

    const fetchBookmarks = useCallback(async () => {
        const refresh = async () => {
            if(userId){
              const data = await getUserBookmarks(userId.toString(), sessionType);
              setBookmarks(data as Bookmark[]);
            }
        };
        if (userId) {
            refresh();
        }
    }, [userId, sessionType]); 
    
    useEffect(() => {
        if (selectedUser && selectedUser.user_id) {
            setUserId(Number(selectedUser.user_id));
            fetchBookmarks();
        }
    }, [selectedUser, fetchBookmarks]);
    
    const getBookmarks = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    fetchBookmarks();
    }, [fetchBookmarks]);

  return (
    <Card className="h-full flex flex-col">
        <CardHeader>    
            <CardTitle>Bookmarks
                <Button onClick={fetchBookmarks} variant="outline" size="icon" className="ml-2 border-0">
                    <ReloadIcon className="w-4 h-3 stroke-4" />
                </Button>
            </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden">
            <div className="flex flex-row justify-between">
                <form onSubmit={getBookmarks} className="flex flex-col w-120 gap-2 items-start">
                    <div className="flex flex-row gap-1 items-center">
                        <Label className="text-sm font-medium w-36">Session type</Label>
                        <Select defaultValue={sessionType} onValueChange={(value) => setSessionType(value as SubscriptionEnum)}>
                            <SelectTrigger>
                            <SelectValue placeholder="Select a session type" />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.values(SubscriptionEnum).map((subscription) => (
                                    <SelectItem value={subscription}>{getSubscriptionDisplayName(subscription)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-row gap-1 items-center">
                        <Label htmlFor="user_id" className="text-sm font-medium w-32">User ID</Label>  
                        <Input type="text" id="user_id" placeholder="User ID" value={userId ?? ''} onChange={(e) => setUserId(Number(e.target.value))} className="border border-gray-300 rounded-md" />
                    </div>
                    <Button type="submit" className="w-36 bg-gray-500 text-white">Search</Button>
                </form>
            </div>
            <ScrollArea type='scroll' className="overflow-y-auto h-full">
                <h1 className="text-lg font-semibold mt-4">Results</h1>
                <DataTable columns={BookmarkTableColumns} data={bookmarks} filterColumns={bookmarkTableFilterColumns} />
            </ScrollArea>
        </CardContent>
    </Card>
  )
}